import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { AxiosResponse } from 'axios'

import {
  ApiListResponse,
  AssetCommonPrice,
  AssetSecurityMasterPrice,
} from '@types'

import api from '@/store/api'

import { prepareResponseError } from '../utils/helpers'

import useAnalyticsStore from '../analytics'
import { useRepositoriesStore } from '../repositories/index'

export const useAssetPricesStore = defineStore('asset-prices', () => {
  // INIT
  const analyticsStore = useAnalyticsStore()
  const repositoriesStore = useRepositoriesStore()

  const loading = ref(true)
  const loadingSecurityMaster = ref(false)
  const loadingAction = ref(false)

  let abortController = new AbortController()

  const repositoryId = computed(() => repositoriesStore.getCurrentId)

  // ACTIONS
  const fetch = async (asset_id: string) => {
    loading.value = true
    try {
      const result = await api.get<ApiListResponse<AssetCommonPrice[]>>(
        `asset_prices?asset_id=${asset_id}`,
        {
          signal: abortController.signal,
        },
      )
      return result.data.data
    } catch (e) {
      throw Error(prepareResponseError(e))
    } finally {
      loading.value = false
    }
  }

  const fetchSecurityMaster = async (tickers: string) => {
    loadingSecurityMaster.value = true
    try {
      const result = await api.get<AssetSecurityMasterPrice[]>(
        'market_data/daily_prices',
        {
          params: {
            from_dt: '2020-01-01',
            repo_id: repositoryId.value,
            tickers,
          },
          signal: abortController.signal,
        },
      )
      return result.data
    } catch (e) {
      throw Error(prepareResponseError(e))
    } finally {
      loadingSecurityMaster.value = false
    }
  }

  const store = async (price: Partial<AssetCommonPrice>) => {
    loadingAction.value = true
    try {
      const result = await api.post<
        AssetCommonPrice,
        AxiosResponse<AssetCommonPrice>,
        Partial<AssetCommonPrice>
      >('asset_prices', price)
      analyticsStore.markAsDeprecated()
      return result.data
    } catch (e) {
      throw Error(prepareResponseError(e))
    } finally {
      loadingAction.value = false
    }
  }

  const update = async (price: Partial<AssetCommonPrice>) => {
    loadingAction.value = true
    try {
      const result = await api.put<
        AssetCommonPrice,
        AxiosResponse<AssetCommonPrice>,
        Partial<AssetCommonPrice>
      >(`asset_prices/${price.id}`, price)
      analyticsStore.markAsDeprecated()
      return result.data
    } catch (e) {
      throw Error(prepareResponseError(e))
    } finally {
      loadingAction.value = false
    }
  }

  const destroy = async (id: number) => {
    loadingAction.value = true
    try {
      await api.delete(`asset_prices/${id}`)
      analyticsStore.markAsDeprecated()
    } catch (e) {
      throw Error(prepareResponseError(e))
    } finally {
      loadingAction.value = false
    }
  }

  const cancel = () => {
    abortController.abort()
    abortController = new AbortController()
  }

  return {
    loading,
    loadingSecurityMaster,
    loadingAction,

    fetch,
    fetchSecurityMaster,

    store,
    update,
    destroy,

    cancel,
  }
})
